import { DateTime } from 'luxon';
import { SubjectData } from 'shell/models/person';
import { ParenthoodVM } from 'shell/view-models/parenthood';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';
import { IdParenthood } from 'shell/constants/id-parenthood';

export const transformParenthood = (
  person: SubjectData
): [ParenthoodVM, ParenthoodVM] | undefined => {
  const birthChoiceDateFather = transformToChoiceDate(
    person?.paternita?.generalita?.senzaGiorno,
    person?.paternita?.generalita?.senzaGiornoMese
  );

  const paternity: ParenthoodVM | undefined = person?.paternita
    ? {
        id: IdParenthood.Paternity,
        surname: person.paternita?.generalita?.cognome || '',
        name: person.paternita?.generalita?.nome || '',
        fiscalCode: person.paternita?.generalita?.codiceFiscale?.codFiscale || '',
        dateBirth: person.paternita?.generalita?.dataNascita
          ? DateTime.fromISO(person.paternita?.generalita?.dataNascita).toFormat('dd/MM/yyyy')
          : '',
        generality: {
          aire: person.paternita?.generalita?.soggettoAIRE,
          validityFiscalCode: person.paternita?.generalita?.codiceFiscale?.validitaCF,
          validityDateFiscalCode: person.paternita?.generalita?.codiceFiscale
            ?.dataAttribuzioneValidita
            ? DateTime.fromISO(
                person.paternita?.generalita?.codiceFiscale?.dataAttribuzioneValidita
              )
            : null,
          gender: person.paternita?.generalita?.sesso || '',
          expatriationYear: person.paternita?.generalita?.annoEspatrio || '',
          birthDate: person.paternita?.generalita?.dataNascita
            ? DateTime.fromISO(person.paternita?.generalita?.dataNascita)
            : null,
          // senza giorno mese, etc
          birthChoiceDate: birthChoiceDateFather,
          isForeign: !person.paternita?.generalita?.luogoNascita?.comune,
          // generality italian form
          italian: {
            // location:
            //   person.paternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
            province: person.paternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            // municipality: person.paternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
            municipality: person.paternita?.generalita?.luogoNascita?.comune
              ? {
                  description: person.paternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
                  id: person.paternita?.generalita?.luogoNascita?.comune?.codiceIstat || '',
                  province:
                    person.paternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
                }
              : undefined,
          },
          // generality foreign form
          foreign: {
            state: person.paternita?.generalita?.luogoNascita?.localita
              ? {
                  id: person.paternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
                  value:
                    person.paternita?.generalita?.luogoNascita?.localita?.descrizioneStato || '',
                }
              : undefined,
            province: person.paternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
            municipality: person.paternita?.generalita?.luogoNascita?.localita
              ? {
                  description:
                    person.paternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
                  province:
                    person.paternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                }
              : undefined,
          },
          // Other fields generality
          exceptionalBirthPlace: person.paternita?.generalita?.luogoNascita?.luogoEccezionale || '',
          municipalityIdentifier:
            person.paternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
          istatCode:
            person.paternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat || '',
          anprIdentifier: person.paternita?.generalita?.idSchedaSoggettoANPR || '',
          note: person.paternita?.generalita?.note || '',
        },
        person: {
          fiscalCode: person.paternita?.generalita?.codiceFiscale?.codFiscale || '',
          name: person.paternita?.generalita?.nome || '',
          surname: person.paternita?.generalita?.cognome || '',
          withoutName: person.paternita?.generalita?.senzaNome === '1' ? true : false,
          withoutSurname: person.paternita?.generalita?.senzaCognome === '1' ? true : false,
        },
        personInformation: {
          maritalStatus: person.paternita?.statoCivile?.statoCivile || '',
          withoutMaritalStatus: person.paternita?.statoCivile?.statoCivileND === '1' ? true : false,
          maritalStatusNote: person.paternita?.statoCivile?.noteStatoCivile || '',
          code: person.paternita?.cittadinanza?.codiceStato || '',
          citizenship: person.paternita?.cittadinanza?.descrizioneStato || '',
          validityFrom: person.paternita?.cittadinanza?.dataValidita
            ? DateTime.fromISO(person.paternita?.cittadinanza?.dataValidita)
            : null,
          citizenshipNote: person.paternita?.cittadinanza?.noteStato || '',
          qualification: person.paternita?.altriDati?.titoloStudio || '',
          jobPosition: person.paternita?.altriDati?.posizioneProfessionale || '',
          conditionNotProfessional: person.paternita?.altriDati?.condizioneNonProfessionale || '',
        },
      }
    : undefined;

  const birthChoiceDateMother = transformToChoiceDate(
    person?.maternita?.generalita?.senzaGiorno,
    person?.maternita?.generalita?.senzaGiornoMese
  );

  const maternity: ParenthoodVM | undefined = person?.maternita
    ? {
        id: IdParenthood.Maternity,
        surname: person.maternita?.generalita?.cognome || '',
        name: person.maternita?.generalita?.nome || '',
        fiscalCode: person.maternita?.generalita?.codiceFiscale?.codFiscale || '',
        dateBirth: person.maternita?.generalita?.dataNascita
          ? DateTime.fromISO(person.maternita?.generalita?.dataNascita).toFormat('dd/MM/yyyy')
          : '',
        generality: {
          aire: person.maternita?.generalita?.soggettoAIRE,
          validityFiscalCode: person.maternita?.generalita?.codiceFiscale?.validitaCF,
          validityDateFiscalCode: person.maternita?.generalita?.codiceFiscale
            ?.dataAttribuzioneValidita
            ? DateTime.fromISO(
                person.maternita?.generalita?.codiceFiscale?.dataAttribuzioneValidita
              )
            : null,
          gender: person.maternita?.generalita?.sesso || '',
          expatriationYear: person.maternita?.generalita?.annoEspatrio || '',
          birthDate: person.maternita?.generalita?.dataNascita
            ? DateTime.fromISO(person.maternita?.generalita?.dataNascita)
            : null,
          // senza giorno mese, etc
          birthChoiceDate: birthChoiceDateMother,
          // generality italian form
          italian: {
            // location:
            // person.maternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
            province: person.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            // municipality: person.maternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
            municipality: person.maternita?.generalita?.luogoNascita?.comune
              ? {
                  description: person.maternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
                  id: person.maternita?.generalita?.luogoNascita?.comune?.codiceIstat || '',
                  province:
                    person.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
                }
              : undefined,
          },
          // generality foreign form
          foreign: {
            state: person.maternita?.generalita?.luogoNascita?.localita
              ? {
                  id: person.maternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
                  value:
                    person.maternita?.generalita?.luogoNascita?.localita?.descrizioneStato || '',
                }
              : undefined,
            province: person.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            municipality: person.maternita?.generalita?.luogoNascita?.localita
              ? {
                  description:
                    person.maternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
                  province:
                    person.maternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                }
              : undefined,
          },
          // Other fields generality
          exceptionalBirthPlace: person.maternita?.generalita?.luogoNascita?.luogoEccezionale || '',
          municipalityIdentifier:
            person.maternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
          istatCode:
            person.maternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat || '',
          anprIdentifier: person.maternita?.generalita?.idSchedaSoggettoANPR || '',
          note: person.maternita?.generalita?.note || '',
        },
        person: {
          fiscalCode: person.maternita?.generalita?.codiceFiscale?.codFiscale || '',
          name: person.maternita?.generalita?.nome || '',
          surname: person.maternita?.generalita?.cognome || '',
          withoutName: person.maternita?.generalita?.senzaCognome === '1' ? true : false,
          withoutSurname: person.maternita?.generalita?.senzaNome === '1' ? true : false,
        },
        personInformation: {
          maritalStatus: person.maternita?.statoCivile?.statoCivile || '',
          withoutMaritalStatus: person.maternita?.statoCivile?.statoCivileND === '1' ? true : false,
          maritalStatusNote: person.maternita?.statoCivile?.noteStatoCivile || '',
          code: person.maternita?.cittadinanza?.codiceStato || '',
          citizenship: person.maternita?.cittadinanza?.descrizioneStato || '',
          validityFrom: person.maternita?.cittadinanza?.dataValidita
            ? DateTime.fromISO(person.maternita?.cittadinanza?.dataValidita)
            : null,
          citizenshipNote: person.maternita?.cittadinanza?.noteStato || '',
          qualification: person.maternita?.altriDati?.titoloStudio || '',
          jobPosition: person.maternita?.altriDati?.posizioneProfessionale || '',
          conditionNotProfessional: person.maternita?.altriDati?.condizioneNonProfessionale || '',
        },
      }
    : undefined;

  const res = [];

  if (paternity) {
    res.push(paternity);
  }

  if (maternity) {
    res.push(maternity);
  }

  return res.length > 0 ? (res as [ParenthoodVM, ParenthoodVM]) : undefined;
};
